// @ts-check

import { html, render } from "lit-html";
import { createRenderScheduler } from "./render_scheduler.js";
import { Router, normalizeRoutePath } from "./router.js";
import { setupServiceWorkerReloadWhenIdle } from "./sw-reload.js";
import { TOOLS } from "./tools/index.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {typeof TOOLS[number]} ToolDefinition */
/** @typedef {import("./tools/index.js").ToolState} ToolState */
/** @typedef {import("./tools/index.js").ToolEnv} ToolEnv */

const HOME_ROUTE = "/";

/** @type {HTMLElement | null} */
const root = document.getElementById("app");

/** @type {Map<string, ToolDefinition>} */
const toolByPath = new Map();
for (let i = 0, total = TOOLS.length; i < total; i += 1) {
  const tool = TOOLS[i];
  toolByPath.set(tool.path, tool);
}

/** @type {Record<string, ToolState>} */
const toolStates = {};
for (let i = 0, total = TOOLS.length; i < total; i += 1) {
  const tool = TOOLS[i];
  toolStates[tool.key] = tool.createState();
}

const state = {
  route: normalizeRoutePath(window.location.pathname),
};

const renderScheduler = createRenderScheduler(renderApp);
/** @type {Record<string, number>} */
const statusTimeoutIds = {};

main();

function main() {
  if (!(root instanceof HTMLElement)) {
    return;
  }

  Router.init();
  window.addEventListener("app:navigate", handleNavigate);
  window.setInterval(handleLiveTick, 1000);
  renderApp();
  void setupServiceWorkerReloadWhenIdle({ isIdle: isAppIdle });
}

/**
 * @param {Event} event
 * @returns {void}
 */
function handleNavigate(event) {
  const navigateEvent = /** @type {CustomEvent<{ path?: unknown }>} */ (event);
  const pathCandidate = navigateEvent.detail?.path;
  const routeText =
    typeof pathCandidate === "string"
      ? pathCandidate
      : window.location.pathname;
  const nextRoute = normalizeRoutePath(routeText);
  state.route = nextRoute;
  syncDocumentTitle();
  scheduleRender();
}

function handleLiveTick() {
  if (document.visibilityState !== "visible") {
    return;
  }

  const currentTool = getCurrentTool();
  if (!currentTool) {
    return;
  }

  const refreshTool =
    /** @type {{ shouldAutoRefresh?: (state: ToolState) => boolean }} */ (
      /** @type {unknown} */ (currentTool)
    );
  const shouldAutoRefresh = refreshTool.shouldAutoRefresh;
  if (!shouldAutoRefresh) {
    return;
  }

  const toolState = toolStates[currentTool.key];
  if (!shouldAutoRefresh(toolState)) {
    return;
  }

  scheduleRender();
}

async function scheduleRender() {
  renderScheduler.requestUpdate();

  let renderComplete = false;
  while (!renderComplete) {
    renderComplete = await renderScheduler.getUpdateComplete();
  }
}

function renderApp() {
  if (!(root instanceof HTMLElement)) {
    return;
  }

  syncDocumentTitle();
  render(appView(), root);
}

function syncDocumentTitle() {
  const currentTool = getCurrentTool();
  let nextTitle = "Tools";
  if (currentTool) {
    nextTitle = `${currentTool.title} | Tools`;
  }

  if (!currentTool && state.route !== HOME_ROUTE) {
    nextTitle = "Not Found | Tools";
  }

  document.title = nextTitle;
}

function isAppIdle() {
  return state.route === HOME_ROUTE;
}

/**
 * @returns {ToolDefinition | null}
 */
function getCurrentTool() {
  const currentTool = toolByPath.get(state.route);
  if (!currentTool) {
    return null;
  }

  return currentTool;
}

/**
 * @returns {LitView}
 */
function appView() {
  const currentTool = getCurrentTool();
  const pageView = renderPageView(currentTool);

  return html`
    <main class="app-safe-shell mx-auto min-h-dvh w-full max-w-5xl">
      ${pageView}
    </main>
  `;
}

/**
 * @param {ToolDefinition | null} currentTool
 * @returns {LitView}
 */
function renderPageView(currentTool) {
  if (state.route === HOME_ROUTE) {
    return renderHomeView();
  }

  if (!currentTool) {
    return renderNotFoundView();
  }

  const toolState = toolStates[currentTool.key];
  const renderTool =
    /** @type {(state: ToolState, env: ToolEnv) => LitView} */ (
      currentTool.render
    );
  const pageBody = renderTool(toolState, buildToolEnv());

  return html`
    <div class="border-b border-stone-200 pb-4">
      <a
        class="text-sm text-stone-400 transition hover:text-stone-600"
        data-router
        data-destination="/"
        href="/"
      >
        ← Tools
      </a>
      <h1 class="mt-2 text-2xl font-semibold tracking-tight text-stone-900">
        ${currentTool.title}
      </h1>
    </div>
    <div class="mt-4">${pageBody}</div>
  `;
}

function renderHomeView() {
  /** @type {LitView[]} */
  const items = [];
  for (let i = 0, total = TOOLS.length; i < total; i += 1) {
    const tool = TOOLS[i];
    items.push(html`
      <li>
        <a
          class="group grid border-b border-stone-100 py-4 sm:grid-cols-[15rem_1fr] sm:items-baseline sm:gap-8"
          data-router
          data-destination=${tool.path}
          href=${tool.path}
        >
          <span
            class="font-medium text-stone-900 transition group-hover:text-stone-500"
          >
            ${tool.title}
          </span>
          <span class="mt-0.5 text-sm text-stone-400 sm:mt-0"
            >${tool.blurb}</span
          >
        </a>
      </li>
    `);
  }

  return html`
    <div>
      <h1 class="text-2xl font-semibold tracking-tight text-stone-900">
        Tools
      </h1>
      <ul class="mt-6">
        ${items}
      </ul>
    </div>
  `;
}

function renderNotFoundView() {
  return html`
    <div>
      <a
        class="text-sm text-stone-400 transition hover:text-stone-600"
        data-router
        data-destination="/"
        href="/"
      >
        ← Tools
      </a>
      <h1 class="mt-4 text-xl font-semibold text-stone-900">Page not found</h1>
      <p class="mt-2 text-sm text-stone-500">This tool doesn't exist.</p>
    </div>
  `;
}

/**
 * @returns {ToolEnv}
 */
function buildToolEnv() {
  return {
    scheduleRender,
    handleFieldInput,
    handleCheckboxInput,
    setTemporaryStatus,
  };
}

/**
 * @param {ToolState} record
 * @param {string} field
 * @param {Event} event
 * @returns {void}
 */
function handleFieldInput(record, field, event) {
  const target = event.target;
  if (
    !(target instanceof HTMLInputElement) &&
    !(target instanceof HTMLTextAreaElement) &&
    !(target instanceof HTMLSelectElement)
  ) {
    return;
  }

  record[field] = target.value;
  scheduleRender();
}

/**
 * @param {ToolState} record
 * @param {string} field
 * @param {Event} event
 * @returns {void}
 */
function handleCheckboxInput(record, field, event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }

  record[field] = target.checked;
  scheduleRender();
}

/**
 * @param {ToolState} record
 * @param {string} field
 * @param {string} nextValue
 * @param {string} resetValue
 * @param {string} timerKey
 * @returns {void}
 */
function setTemporaryStatus(record, field, nextValue, resetValue, timerKey) {
  const existingTimeoutId = statusTimeoutIds[timerKey] || 0;
  if (existingTimeoutId) {
    window.clearTimeout(existingTimeoutId);
  }

  record[field] = nextValue;
  scheduleRender();

  const timeoutId = window.setTimeout(function () {
    record[field] = resetValue;
    scheduleRender();
  }, 2000);

  statusTimeoutIds[timerKey] = timeoutId;
}
